import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import {
  fetchDepartmentSales,
  fetchHourlySales,
  fetchTopItems,
  getDSMStoreMapping
} from "src/api/bi";
import { RealTimeSalesReportInitialState, RequestParams } from "./interface";
import { getStartAndEndDatesOfPeriod } from "src/utils/getDates";
import { getViewedStores } from "src/api/sp/index";
import { resetStore } from "src/store/actions";
import { sorter } from "src/utils/sorter";
import { getSortOrder } from "src/utils/tableFunctions";
import { ITEM_TYPES } from "src/config/constants/genericConstants";

const DEFAULT_PERIOD = "T";
const DEFAULT_STORE_TYPE = "1";
const [startDate, endDate] = getStartAndEndDatesOfPeriod(DEFAULT_PERIOD);

export const realTimeSalesReportInitialState: RealTimeSalesReportInitialState =
  {
    realtimeSalesFilterParams: {
      storeType: DEFAULT_STORE_TYPE,
      date: "",
      fromDate: startDate,
      toDate: endDate,
      storeIds: [],
      sortParam: "sales",
      sortOrder: "DESC",
      dsm: ""
    },
    searchQuery: "",
    departmentSales: null,
    hourlySales: null,
    dsmData: [],
    storeListData: [],
    storeListDataOriginal: [],
    topItems: null,
    topItemsView: null,
    topItemsCopy: null,
    topItemsCount: 25,
    departmentsApiStatus: "idle",
    hourlySalesApiStatus: "idle",
    listDataApiStatus: "idle",
    topItemsApiStatus: "idle",
    viewedStoreIds: [],
    syncTopItems: false,
    DSMStoreMapping: [],
    topItemsSort: {
      sortParam: "sales",
      sortOrder: "DESC"
    },
    itemType: ""
  };

export const getDepartmentSales = createAsyncThunk(
  "getDepartmentSales",
  (params: RequestParams & { signal: AbortSignal }) =>
    fetchDepartmentSales(params)
);

export const getHourlySales = createAsyncThunk(
  "getHourlySales",
  (params: RequestParams & { signal: AbortSignal }) => fetchHourlySales(params)
);

export const getDSMStoreMappingData = createAsyncThunk(
  "getDSMStoreMappingData",
  () => getDSMStoreMapping()
);

export const getTopItems = createAsyncThunk("getTopItems", (params: any) =>
  fetchTopItems(params)
);

export const fetchViewedStores = createAsyncThunk(
  "fetchViewedStores",
  (params: any) => getViewedStores(params)
);

const realTimeSalesReportSlice = createSlice({
  initialState: realTimeSalesReportInitialState,
  name: "realTimeSalesReport",
  reducers: {
    // Toggle last year sales
    setRealtimeLySales(state: RealTimeSalesReportInitialState, action: any) {
      state.departmentSales.realtimeLySales = action.payload;
    },
    // Update filter params in store
    setFilterParams(state: RealTimeSalesReportInitialState, action: any) {
      state.realtimeSalesFilterParams = action.payload;
    },
    setStoreType(state: RealTimeSalesReportInitialState, action: any) {
      const { storeType, defaultStoreId } = action.payload;

      const defaultDsm: any = state.DSMStoreMapping.find(
        (dsm) => dsm.storeId === defaultStoreId
      );
      const defaultDsmId = defaultDsm?.dsmId;

      state.realtimeSalesFilterParams.storeType = storeType;
      state.realtimeSalesFilterParams.storeIds = [defaultStoreId];
      state.realtimeSalesFilterParams.dsm = defaultDsmId;

      if (storeType === "2") {
        const storesUnderDefaultDsm = state.DSMStoreMapping.filter(
          (store) => store.dsmId === defaultDsmId
        ).map((store) => ({ id: store.storeId, name: store.storeName }));
        sorter(storesUnderDefaultDsm, "name", "ASC");
        state.storeListData = storesUnderDefaultDsm;
      } else {
        state.storeListData = [...state.storeListDataOriginal];
      }
    },
    setDSMRegion(state: RealTimeSalesReportInitialState, action: any) {
      const dsm = action.payload;
      state.realtimeSalesFilterParams.dsm = dsm;
      state.realtimeSalesFilterParams.storeIds = [];
      const storesUnderDsm = state.DSMStoreMapping.filter(
        (store) => store.dsmId === dsm
      ).map((store) => ({ id: store.storeId, name: store.storeName }));
      sorter(storesUnderDsm, "name", "ASC");
      state.storeListData = storesUnderDsm;
    },
    // Update topItems count
    setTopItemsCount(state: RealTimeSalesReportInitialState, action: any) {
      const { topItemsCount, itemType } = action.payload;

      state.topItemsCount = topItemsCount;
      const topItems = state.topItemsCopy?.slice(0, state.topItemsCount);

      state.topItems = itemType
        ? topItems?.filter(
            (item) =>
              item?.tags?.includes(ITEM_TYPES.CORE) ||
              item?.tags?.includes(ITEM_TYPES.CORE_ITEM)
          )
        : topItems;
    },
    // Search items
    searchItems(state: RealTimeSalesReportInitialState, action: any) {
      const { searchQuery } = action.payload;
      state.searchQuery = searchQuery;
      const itemType = state.itemType;
      const searchResults = state?.topItemsCopy
        ?.slice(0, state.topItemsCount)
        ?.filter((item) => {
          if (itemType) {
            return (
              item?.tags?.includes(ITEM_TYPES.CORE) ||
              item?.tags?.includes(ITEM_TYPES.CORE_ITEM)
            );
          }
          return true;
        })
        ?.filter((item) => {
          return (
            item?.itemId?.toString().includes(searchQuery) ||
            item?.UPC?.toString().includes(searchQuery) ||
            item?.description?.toLowerCase().includes(searchQuery.toLowerCase())
          );
        });
      state.topItems = searchResults;
    },
    setTopItemsSync(state) {
      state.syncTopItems = !current(state).syncTopItems;
    },
    updateTopItemsList(state: RealTimeSalesReportInitialState, action: any) {
      state.topItems.forEach((topItem: any) => {
        if (topItem.itemId === action.payload) {
          topItem.wishlist = !topItem?.wishlist;
        }
      });
    },
    resetState() {
      return realTimeSalesReportInitialState;
    },
    setTopItemSort: (state: RealTimeSalesReportInitialState, action: any) => {
      const sortParam = action.payload;
      const sortOrder = getSortOrder(sortParam, state.topItemsSort);
      state.topItemsSort = {
        sortParam,
        sortOrder
      };
      state.realtimeSalesFilterParams = {
        ...state.realtimeSalesFilterParams,
        sortParam,
        sortOrder
      };
    },
    setItemType: (state: RealTimeSalesReportInitialState, action: any) => {
      const { itemType } = action.payload;

      state.itemType = itemType;

      if (itemType) {
        const items = state?.topItemsCopy
          ?.slice(0, state.topItemsCount)
          ?.filter(
            (item) =>
              item?.tags?.includes(ITEM_TYPES.CORE) ||
              item?.tags?.includes(ITEM_TYPES.CORE_ITEM)
          );
        state.topItems = items;
      }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return realTimeSalesReportInitialState;
      })
      .addCase(getDepartmentSales.pending, (state) => {
        state.departmentsApiStatus = "pending";
        state.departmentSales = null;
      })
      .addCase(getDepartmentSales.fulfilled, (state, { payload }) => {
        state.departmentSales = payload.data;
        state.departmentsApiStatus = "idle";
      })
      .addCase(getDepartmentSales.rejected, (state) => {
        state.departmentsApiStatus = "error";
        state.departmentSales = null;
      })
      .addCase(getHourlySales.pending, (state) => {
        state.hourlySalesApiStatus = "pending";
        state.hourlySales = null;
      })
      .addCase(getHourlySales.fulfilled, (state, { payload }) => {
        state.hourlySales = payload.data;
        state.hourlySalesApiStatus = "idle";
      })
      .addCase(getHourlySales.rejected, (state) => {
        state.hourlySalesApiStatus = "error";
        state.hourlySales = null;
      })
      .addCase(getDSMStoreMappingData.pending, (state) => {
        state.listDataApiStatus = "pending";
      })
      .addCase(getDSMStoreMappingData.fulfilled, (state, { payload }) => {
        state.listDataApiStatus = "idle";
        if (payload.data?.length) {
          state.DSMStoreMapping = payload.data;

          const dsmData = payload.data
            .filter(
              (dsm: any, idx: number, self: any) =>
                idx === self.findIndex((t: any) => t.dsmId === dsm.dsmId)
            )
            .map((dsm: any) => ({ id: dsm.dsmId, name: dsm.dsmName }));

          const storeData = payload.data.map((store: any) => ({
            id: store.storeId,
            name: store.storeName
          }));

          sorter(dsmData, "name", "ASC");
          sorter(storeData, "name", "ASC");

          state.dsmData = dsmData;
          state.storeListData = [...storeData];
          state.storeListDataOriginal = [...storeData];
        }
      })
      .addCase(getDSMStoreMappingData.rejected, (state) => {
        state.listDataApiStatus = "error";
        state.DSMStoreMapping = [];
        state.dsmData = [];
        state.storeListData = [];
      })
      .addCase(getTopItems.pending, (state) => {
        state.topItemsApiStatus = "pending";
      })
      .addCase(getTopItems.fulfilled, (state, { payload }) => {
        state.topItemsApiStatus = "idle";
        payload.data = payload.data?.map((item: any, index: any) => ({
          ...item,
          itemHash: item.itemId,
          uniqueId: index
        }));
        state.topItems = payload.data?.slice(0, state.topItemsCount);
        state.topItemsCopy = payload.data;
      })
      .addCase(getTopItems.rejected, (state, action) => {
        if (action?.error?.message === "ERR_CANCELED") {
          state.topItemsApiStatus = "pending";
        } else {
          state.topItemsApiStatus = "error";
          state.topItems = null;
          state.topItemsCopy = null;
        }
      })
      .addCase(fetchViewedStores.fulfilled, (state, { payload }) => {
        state.viewedStoreIds = payload.data.viewedStores;
      });
  }
});

export const selectDate = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.realtimeSalesFilterParams?.date;

export const selectPosSales = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.departmentSales?.POSSales;

export const selectTopItems = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.topItems;

export const selectPosLySales = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.departmentSales?.POSLYSales;

export const selectCustomerCount = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.hourlySales?.customerCount;

export const selectAverageRing = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.hourlySales?.averageRing;

export const selectHourlySalesData = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.hourlySales?.hourlySales;

export const selectTotalHourleySales = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.hourlySales?.total;

export const selectTopItemsApiStatus = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.topItemsApiStatus;

export const selectTopItemsCount = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.topItemsCount;

export const selectTopItemsSort = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.topItemsSort;
export const selectFilterParams = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport?.realtimeSalesFilterParams;

export const selectRealtimeLySalesSelection = ({
  realtimeSalesReport
}: {
  realtimeSalesReport: RealTimeSalesReportInitialState;
}) => realtimeSalesReport.departmentSales?.realtimeLySales;

export const {
  setRealtimeLySales,
  setFilterParams,
  setTopItemsCount,
  searchItems,
  setTopItemsSync,
  setStoreType,
  setDSMRegion,
  setTopItemSort,
  updateTopItemsList,
  resetState,
  setItemType
} = realTimeSalesReportSlice.actions;

export default realTimeSalesReportSlice.reducer;
